import { useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { type SupportedLanguage, formatTranslation } from '../../utils/translations';

export type PluginTranslationKey =
  | 'panel.title'
  | 'panel.description'
  | 'guidance.title'
  | 'guidance.beta_vs_published'
  | 'guidance.account_wide_events'
  | 'guidance.events_fallback'
  | 'table.client'
  | 'table.permissions'
  | 'table.connected'
  | 'table.status'
  | 'table.actions'
  | 'status.active'
  | 'status.revoked'
  | 'action.revoke'
  | 'action.revoking'
  | 'state.loading'
  | 'state.empty'
  | 'state.empty_hint'
  | 'state.error'
  | 'state.revoke_error'
  | 'state.revoke_success'
  | 'pagination.prev'
  | 'pagination.next'
  | 'pagination.page'
  | 'scope.read'
  | 'scope.write'
  | 'scope.events';

const en: Record<PluginTranslationKey, string> = {
  'panel.title': 'Connected AI & Plugin Integrations',
  'panel.description': 'Manage external AI assistants and MCP clients connected to your PriceLoss account.',
  'guidance.title': 'Integration Guide & Policies',
  'guidance.beta_vs_published':
    'Beta vs Marketplace: During private pilot/beta, clients connect via registered developer endpoints. Once publicly listed, add PriceLoss directly from the host marketplace.',
  'guidance.account_wide_events':
    'Account-Wide Events: Price drop alerts across all your watches are delivered to your connected client. Host-side filters control which notifications appear in your chat.',
  'guidance.events_fallback':
    'Proactive Events & Fallback: If push events are unsupported or disabled by your host, periodic tool checks continue to work, and email/extension notifications remain active as fallback.',
  'table.client': 'Client',
  'table.permissions': 'Permissions',
  'table.connected': 'Connected',
  'table.status': 'Status',
  'table.actions': 'Actions',
  'status.active': 'Active',
  'status.revoked': 'Revoked',
  'action.revoke': 'Revoke',
  'action.revoking': 'Revoking...',
  'state.loading': 'Loading connected integrations...',
  'state.empty': 'No connected integrations found.',
  'state.empty_hint': 'When you connect PriceLoss to an external assistant or MCP host, it will appear here.',
  'state.error': 'Failed to load integrations. Please try again.',
  'state.revoke_error': 'Failed to revoke integration. Please try again.',
  'state.revoke_success': 'Integration revoked successfully.',
  'pagination.prev': 'Previous',
  'pagination.next': 'Next',
  'pagination.page': 'Page {{page}}',
  'scope.read': 'Read watches',
  'scope.write': 'Manage watches',
  'scope.events': 'Price drop events',
};

const ar: Record<PluginTranslationKey, string> = {
  'panel.title': 'عمليات الربط بالذكاء الاصطناعي والإضافات',
  'panel.description': 'إدارة المساعدين الأذكياء وعملاء MCP المتصلين بحسابك في PriceLoss.',
  'guidance.title': 'دليل وتوجيهات التكامل',
  'guidance.beta_vs_published':
    'النسخة التجريبية مقابل المتجر: أثناء المرحلة التجريبية، تتصل التطبيقات عبر نقاط نهاية المطورين المعتمدة. عند اعتماد الإدراج العام، يمكنك إضافة PriceLoss مباشرة من متجر المضيف.',
  'guidance.account_wide_events':
    'أحداث على مستوى الحساب: تصل تنبيهات انخفاض الأسعار لجميع مراقباتك إلى العميل المتصل. تُحدد الفلاتر لدى المضيف أي الإشعارات تظهر في محادثتك.',
  'guidance.events_fallback':
    'الأحداث الاستباقية والبديل: إذا كانت الإشعارات الفورية غير مدعومة أو معطلة من المضيف، يستمر فحص الأدوات الدوري في العمل، وتظل تنبيهات البريد والإضافة فعالة كبديل موثوق.',
  'table.client': 'العميل',
  'table.permissions': 'الصلاحيات',
  'table.connected': 'تاريخ الربط',
  'table.status': 'الحالة',
  'table.actions': 'الإجراءات',
  'status.active': 'نشط',
  'status.revoked': 'ملغى',
  'action.revoke': 'إلغاء الربط',
  'action.revoking': 'جارٍ الإلغاء...',
  'state.loading': 'جارٍ تحميل عمليات الربط...',
  'state.empty': 'لم يتم العثور على أي عمليات ربط متصلة.',
  'state.empty_hint': 'عند ربط PriceLoss بمساعد خارجي أو منصة MCP، ستظهر هنا.',
  'state.error': 'فشل تحميل عمليات الربط. يرجى المحاولة مرة أخرى.',
  'state.revoke_error': 'فشل إلغاء عملية الربط. يرجى المحاولة مرة أخرى.',
  'state.revoke_success': 'تم إلغاء عملية الربط بنجاح.',
  'pagination.prev': 'السابق',
  'pagination.next': 'التالي',
  'pagination.page': 'صفحة {{page}}',
  'scope.read': 'قراءة المراقبات',
  'scope.write': 'إدارة المراقبات',
  'scope.events': 'إشعارات انخفاض الأسعار',
};

const fr: Record<PluginTranslationKey, string> = {
  'panel.title': 'Intégrations IA et Plugins',
  'panel.description': 'Gérez les assistants IA externes et clients MCP connectés à votre compte PriceLoss.',
  'guidance.title': "Guide d'intégration et politiques",
  'guidance.beta_vs_published':
    'Bêta vs Marketplace: En pilote privé, les clients se connectent via des points de terminaison développeur enregistrés. Après publication officielle, ajoutez PriceLoss depuis la marketplace hôte.',
  'guidance.account_wide_events':
    "Événements sur tout le compte: Les alertes de baisse de prix sur toutes vos surveillances sont transmises à votre client connecté. Les filtres de l'hôte gèrent les notifications affichées.",
  'guidance.events_fallback':
    "Événements proactifs et repli: Si les notifications push ne sont pas prises en charge ou sont désactivées, les vérifications périodiques d'outils continuent et les alertes email/extension restent actives.",
  'table.client': 'Client',
  'table.permissions': 'Permissions',
  'table.connected': 'Connecté',
  'table.status': 'Statut',
  'table.actions': 'Actions',
  'status.active': 'Actif',
  'status.revoked': 'Révoqué',
  'action.revoke': 'Révoquer',
  'action.revoking': 'Révocation...',
  'state.loading': 'Chargement des intégrations...',
  'state.empty': 'Aucune intégration connectée trouvée.',
  'state.empty_hint': "Lorsque vous connectez PriceLoss à un assistant externe ou hôte MCP, il s'affiche ici.",
  'state.error': 'Échec du chargement des intégrations. Veuillez réessayer.',
  'state.revoke_error': "Échec de la révocation de l'intégration. Veuillez réessayer.",
  'state.revoke_success': 'Intégration révoquée avec succès.',
  'pagination.prev': 'Précédent',
  'pagination.next': 'Suivant',
  'pagination.page': 'Page {{page}}',
  'scope.read': 'Lecture surveillances',
  'scope.write': 'Gestion surveillances',
  'scope.events': 'Alertes de baisse de prix',
};

const pt: Record<PluginTranslationKey, string> = {
  'panel.title': 'Integrações de IA e Plugins',
  'panel.description': 'Gerencie assistentes de IA externos e clientes MCP conectados à sua conta PriceLoss.',
  'guidance.title': 'Guia de Integração e Políticas',
  'guidance.beta_vs_published':
    'Beta vs Marketplace: Durante o piloto privado, os clientes conectam-se por endpoints registrados de desenvolvedor. Após a aprovação pública, adicione o PriceLoss diretamente da loja do host.',
  'guidance.account_wide_events':
    'Eventos em toda a conta: Os alertas de queda de preço em todos os seus monitoramentos são entregues ao cliente conectado. Os filtros do host controlam as notificações exibidas.',
  'guidance.events_fallback':
    'Eventos proativos e alternativa: Se as notificações push não forem suportadas ou estiverem desativadas, as consultas periódicas continuam ativas e os alertas por e-mail/extensão servem de fallback.',
  'table.client': 'Cliente',
  'table.permissions': 'Permissões',
  'table.connected': 'Conectado',
  'table.status': 'Status',
  'table.actions': 'Ações',
  'status.active': 'Ativo',
  'status.revoked': 'Revogado',
  'action.revoke': 'Revogar',
  'action.revoking': 'Revogando...',
  'state.loading': 'Carregando integrações...',
  'state.empty': 'Nenhuma integração conectada encontrada.',
  'state.empty_hint': 'Quando você conectar o PriceLoss a um assistente externo ou host MCP, ele aparecerá aqui.',
  'state.error': 'Falha ao carregar integrações. Tente novamente.',
  'state.revoke_error': 'Falha ao revogar integração. Tente novamente.',
  'state.revoke_success': 'Integração revogada com sucesso.',
  'pagination.prev': 'Anterior',
  'pagination.next': 'Próximo',
  'pagination.page': 'Página {{page}}',
  'scope.read': 'Ler monitoramentos',
  'scope.write': 'Gerenciar monitoramentos',
  'scope.events': 'Eventos de queda de preço',
};

const es: Record<PluginTranslationKey, string> = {
  'panel.title': 'Integraciones de IA y Plugins',
  'panel.description': 'Administre los asistentes de IA externos y clientes MCP conectados a su cuenta de PriceLoss.',
  'guidance.title': 'Guía de Integración y Políticas',
  'guidance.beta_vs_published':
    'Beta vs Marketplace: En la fase piloto privada, los clientes se conectan mediante puntos finales de desarrollador registrados. Tras la aprobación pública, agregue PriceLoss directamente desde la tienda del anfitrión.',
  'guidance.account_wide_events':
    'Eventos en toda la cuenta: Las alertas de caída de precio de todos sus seguimientos se envían a su cliente conectado. Los filtros del anfitrión controlan qué notificaciones se muestran.',
  'guidance.events_fallback':
    'Eventos proactivos y respaldo: Si las notificaciones push no son compatibles o están deshabilitadas, las verificaciones periódicas continúan funcionando y las alertas por correo/extensión permanecen como respaldo.',
  'table.client': 'Cliente',
  'table.permissions': 'Permisos',
  'table.connected': 'Conectado',
  'table.status': 'Estado',
  'table.actions': 'Acciones',
  'status.active': 'Activo',
  'status.revoked': 'Revocado',
  'action.revoke': 'Revocar',
  'action.revoking': 'Revocando...',
  'state.loading': 'Cargando integraciones...',
  'state.empty': 'No se encontraron integraciones conectadas.',
  'state.empty_hint': 'Cuando conecte PriceLoss a un asistente externo o anfitrión MCP, aparecerá aquí.',
  'state.error': 'Error al cargar las integraciones. Inténtelo de nuevo.',
  'state.revoke_error': 'Error al revocar la integración. Inténtelo de nuevo.',
  'state.revoke_success': 'Integración revocada con éxito.',
  'pagination.prev': 'Anterior',
  'pagination.next': 'Siguiente',
  'pagination.page': 'Página {{page}}',
  'scope.read': 'Leer seguimientos',
  'scope.write': 'Administrar seguimientos',
  'scope.events': 'Alertas de bajada de precio',
};

const pluginTranslations: Record<SupportedLanguage, Record<PluginTranslationKey, string>> = {
  en,
  ar,
  fr,
  pt,
  es,
};

export function usePluginTranslation(): (
  key: PluginTranslationKey,
  params?: Record<string, string | number>
) => string {
  const { language } = useLanguage();
  return useCallback(
    (key: PluginTranslationKey, params?: Record<string, string | number>): string => {
      const dict = pluginTranslations[language] ?? pluginTranslations.en;
      const raw = dict[key] ?? pluginTranslations.en[key] ?? key;
      return formatTranslation(raw, params);
    },
    [language]
  );
}
